import { useCurrentEntityId } from "@/providers/firebase/authProvider";

import { useUserSettings } from "./useUserSettings";
import { ActWithoutMeSection } from "./sections/ActWithoutMeSection";
import { CadenceSection } from "./sections/CadenceSection";
import { CompanySection } from "./sections/CompanySection";
import { ConnectionsSection } from "./sections/ConnectionsSection";
import { MemorySection } from "./sections/MemorySection";
import { NeedsYouSection } from "./sections/NeedsYouSection";
import { PlanSection } from "./sections/PlanSection";
import { ProfileSection } from "./sections/ProfileSection";
import { RoundSection } from "./sections/RoundSection";
import { SendRulesSection } from "./sections/SendRulesSection";

/**
 * THE PERSON'S SETTINGS (P206 T0202) — the ten things that decide how Raisy behaves for him.
 *
 * WHAT HE DOES, WHAT HE SEES, WHAT HE FEELS. He opens Settings and, on one page, meets every setting
 * that governs the machine on his behalf: **what may act without him · how often it may act · his
 * connections · the rules his sends obey · what needs him · who else is in his company · his profile
 * · his plan and credits · what Raisy remembers about him · his round.** Each row states its value in
 * his own words. Where the setting is a single value it is changed right there; where it is an
 * operation (connecting an account, changing the plan, editing the round) the row states its value
 * and hands him to the page that owns the action. He should feel what Sam and Maya asked for — *"I
 * decide, bots do the toil"* and *"nothing hidden"* — which is why nothing on this page is a link to
 * a form he cannot read the current state of.
 *
 * ⚠️ THIS PAGE REPLACES THE TAXONOMY EDITOR AT `/settings`, AND NOTHING WAS DELETED TO DO IT.
 * Before this build, `/settings` was the inherited CRM template's editor for the demo CRM's own
 * lists — the surface the PO found while in user mode and asked about. It is PRESERVED, admin-gated
 * and lives at `/admin/settings` (T0200, D-206-003/009). This page is what a person gets instead of
 * it, which is the PO's own instruction (IN-05: *"if this is all admin settings then create a new
 * User Settings"*).
 *
 * ⚠️ THREE DEFECTS THIS PAGE IS BUILT NOT TO HAVE, all of them measured before they were fixed
 * elsewhere in this project:
 *   1. **a control that saves nothing** — forbidden outright (D-206-001). Row 5 states its counts
 *      and names the preference that is not built yet rather than rendering a picker that silently
 *      discards a choice;
 *   2. **a failed read rendered as an empty value** — every row distinguishes *reading*, *read*, and
 *      *could not be read*, because "Raisy could not read this now" and "the value is zero" are
 *      different sentences and only one of them is true;
 *   3. **silence after a click** — the primitive shows *Saving…* / *Saved* / *could not save*, from
 *      the retrieved study's finding that a person must always know when a change has landed.
 *
 * The page is deliberately NOT one giant component: the glowtest compliance report for this exact
 * surface records a 1,845-line settings component as 3.7× over the limit, so each of the ten rows is
 * its own module under `sections/` and this file only assembles them.
 */
export const UserSettingsPage = () => {
  const settings = useUserSettings();
  // The company the page belongs to — the same store the rail switches, so the page and the shell
  // never disagree about whose settings these are.
  const entityId = useCurrentEntityId();

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-foreground">Settings</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Everything that decides how Raisy works for you, with the value it holds right now.
        </p>
        {!entityId && (
          <p className="mt-2 text-sm text-muted-foreground">
            Choose a company first — these settings belong to the company in the rail above.
          </p>
        )}
      </header>

      <div className="flex flex-col gap-3">
        <ActWithoutMeSection settings={settings} />
        <CadenceSection settings={settings} />
        <ConnectionsSection settings={settings} />
        <SendRulesSection />
        <NeedsYouSection settings={settings} />
        <CompanySection settings={settings} />
        <ProfileSection settings={settings} />
        <PlanSection settings={settings} />
        <MemorySection settings={settings} />
        <RoundSection settings={settings} />
      </div>
    </div>
  );
};

UserSettingsPage.path = "/settings";
